import type { OperationRun } from '@foodlista/trigger-vue'
import type { OperationPresentationLink } from '../presentation/registry'
import type { OperationItem } from '../types'
import type { OperationStatus } from './lifecycle'
import { getOperationPresenter } from '../presentation/registry'
import { getOperationProgressTranslator } from '../progress/registry'
import { operationTypeLabel } from './boc-type-label'
import {
  hasResumableRun,
  isOperationWorking,
  operationProgress,
  operationStatus,
} from './lifecycle'
import { relativeOperationTime } from './relative-time'

/**
 * `indeterminate` covers every working row the run cannot quantify yet: no
 * progress metadata (queued, or a task that never calls `ctx.progress`) or a
 * phase that has not advanced past zero. A static empty track there reads as
 * a stalled operation, so the row animates instead of claiming 0%.
 */
export type BOCRowProgress =
  | { kind: 'indeterminate' }
  | { kind: 'determinate', ratio: number }

/**
 * `waiting` is a run the platform will resume (parked or retrying); `inactive`
 * is an operation nothing will advance any more — an unrecognised status, or
 * an envelope whose run never arrived.
 */
export type BOCRowStatus = OperationStatus | 'waiting' | 'inactive'

export interface BOCRowView {
  status: BOCRowStatus
  title: string
  subtitle: string
  /** `null` once the run is terminal — a finished row shows no bar. */
  progress: BOCRowProgress | null
  time: string
  link: OperationPresentationLink | null
  dismissable: boolean
}

type Translate = (
  key: string,
  named?: Record<string, unknown>,
  plural?: number,
) => string

function progressSubtitle(
  type: string,
  run: OperationRun | null,
  t: Translate,
): string | undefined {
  const progress = operationProgress(run)
  if (!progress) {
    return undefined
  }

  return getOperationProgressTranslator(type)?.(progress.phase, t)
}

/**
 * Single source of truth for what a BOC row shows. Pure so the composition
 * rules (subtitle precedence, progress, dismissability, deep link) are
 * testable without mounting the component.
 */
export function bocRowView(input: {
  item: OperationItem
  now: Date
  t: Translate
  te: (key: string) => boolean
}): BOCRowView {
  const { item, now, t, te } = input
  const run = item.run
  const lifecycleStatus = operationStatus(run)
  // Working is an allow-list (see PROGRESSING_RUN_STATUSES), so a run outside
  // it shows no bar: it consumes no compute and must not animate forever. It
  // still splits two ways. A resumable run (`WAITING`, `DELAYED`,
  // `PENDING_VERSION`, a retry) executes later, so it reads as waiting and
  // stays undismissable — hiding it would drop its completion handling on
  // reload. Only a run this app cannot place, or an envelope whose run never
  // showed up, is presented as no longer active.
  const working = isOperationWorking(item, now)
  const resumable = hasResumableRun(item)
  const terminal = lifecycleStatus === 'completed'
    || lifecycleStatus === 'failed'
    || lifecycleStatus === 'canceled'
  const status: BOCRowStatus = working || terminal
    ? lifecycleStatus
    : resumable
      ? 'waiting'
      : 'inactive'

  const presentation
    = status === 'completed'
      ? getOperationPresenter(item.type)?.({ item, run, status, t })
      : undefined

  // Failed/canceled/waiting/inactive rows always show the status line: a stale
  // progress phase would otherwise read as if the operation had succeeded.
  const subtitle
    = status === 'failed'
    || status === 'canceled'
    || status === 'waiting'
    || status === 'inactive'
      ? undefined
      : (presentation?.subtitle ?? progressSubtitle(item.type, run, t))

  const progress = working ? operationProgress(run) : undefined
  const ratio = progress && progress.total > 0
    ? Math.min(Math.max(progress.done / progress.total, 0), 1)
    : 0

  return {
    status,
    title: operationTypeLabel(item, t, te),
    subtitle: subtitle ?? t(`operations.boc.status.${status}`),
    progress: working
      ? (ratio > 0 ? { kind: 'determinate', ratio } : { kind: 'indeterminate' })
      : null,
    time: relativeOperationTime(item.createdAt, now, t),
    // `presentation` is only resolved for completed rows, so the deep link
    // cannot leak into a running or failed one.
    link: presentation?.link ?? null,
    dismissable: !working && !resumable,
  }
}
